import React, { useContext, useMemo, useState } from 'react'
import { Grid, GridCell, Text, View } from 'oio-react'
import { useParams } from 'react-router-dom'
import { initiativeUrl } from 'config/constants/urls'
import ContainerBlock from 'src/apps/CustomApp/blocks/Container'
import LayoutContainer from 'src/apps/CustomApp/components/LayoutContainer'
import Title from 'src/apps/CustomApp/components/Title'
import Titlebar from 'src/apps/CustomApp/components/Titlebar'
import ViewModeSwitchControl from 'src/apps/CustomApp/components/ViewModeSwitchControl'
import InitiativeCreateControl from 'src/apps/Initiative/components/CreateControl'
import InitiativeList from 'src/apps/Initiative/components/List'
import { useOrganization } from 'src/graphql/hooks'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import GroupListItemCard from '~/components/ListItemCard'
import GroupListItemRow from '~/components/ListItemRow'

const GroupHome = () => {
   const { initiativeTypeSlug } = useParams()
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const { organization } = useOrganization()
   const groupInitiativeTypes = organization.initiativeTypes
      .filter(t => t.enabled && t.class === 'group')

   const isHeaderGL = false // App is designed for when layout becomes available

   // View Mode - Grid/List View
   const [viewMode, setViewMode] = useState('grid')
   const gridColumnsIfIsHeaderGL = isHeaderGL
      ? '1[a] 2[b] 3[c] 4[d-e] 5[f]'
      : '1[a] 2[b] 3[c-d] 4[e-f]'

   const gridColumns = viewMode === 'grid' ? gridColumnsIfIsHeaderGL : '1'
   const gridSpacing = viewMode === 'grid' ? '15px' : '0px'
   const ListItemComponent = viewMode === 'grid'
      ? GroupListItemCard
      : GroupListItemRow

   // If there is an initiativeTypeSlug, show only groups of that type
   // Otherwise, we assume it is a full Groups app and show all groups of
   // any initiative type
   const initiativeTypes = useMemo(() => {
      if (initiativeTypeSlug) {
         const initiativeType = organization.initiativeTypes
            .find(type => type.slug === initiativeTypeSlug)

         if (initiativeType) {
            return [initiativeType]
         }
      }

      return organization.initiativeTypes
         .filter(type => type.enabled && type.class === 'group')
   }, [organization.initiativeTypes, initiativeTypeSlug])

   const initiativeTypeIds = initiativeTypes.map(type => type.id)
   const appTitle = initiativeTypeSlug && initiativeTypes[0]
      ? initiativeTypes[0].namePlural
      : 'Groups'

   const initiativeListFilter = {
      archived: 'exclude',
      drafts: 'include',
      sortBy: 'name',
      typeIds: initiativeTypeIds,
      limit: 30
   }

   return (
      <LayoutContainer>
         <ContainerBlock>
            <Titlebar>
               <Title color={theme.tmpContentTitleColor}>{appTitle}</Title>
               <View display="flex">
                  <ViewModeSwitchControl
                     currentViewMode={viewMode}
                     onListButtonClick={() => setViewMode('list')}
                     onGridButtonClick={() => setViewMode('grid')}
                  />
                  {organization.currentUserCanEdit && (
                     <View
                        display="block"
                        height="30px"
                        borderRight={`1px solid ${theme.tmpContentLineColor}`}
                        marginRight="15px"
                        marginLeft="15px"
                     />
                  )}
                  <InitiativeCreateControl initiativeTypes={groupInitiativeTypes} />
               </View>
            </Titlebar>
            <View marginTop={viewMode === 'grid' && theme.tmpSpacing}>
               <Grid columns={gridColumns} spacing={gridSpacing}>
                  <InitiativeList
                     emptyContent={(
                        <GridCell colspan={gridColumns}>
                           <View width="100%" padding="30px 0px">
                              <Text size="2" color="#888">
                                 {`No ${appTitle} to show`}
                              </Text>
                           </View>
                        </GridCell>
                     )}
                     filter={initiativeListFilter}>
                     {initiativeList => initiativeList.items.map((initiative) => {
                        const numMembers = initiative.roles.find(r => r.type === 'primary')?.numActiveUsers

                        return (
                           <GridCell key={initiative.id}>
                              <ListItemComponent
                                 borderColor={theme.tmpBorderColor}
                                 coverMedia={initiative.coverMedia}
                                 dateAdded={initiative.dateAdded}
                                 linkTo={initiativeUrl(initiative)}
                                 members={initiative.members}
                                 name={initiative.name}
                                 numMembers={numMembers}
                                 privacy={initiative.privacy}
                                 subtitle={initiative.subtitle}
                                 textColor={theme.tmpNavTextColor}
                              />
                           </GridCell>
                        )
                     })}
                  </InitiativeList>
               </Grid>
            </View>
         </ContainerBlock>
      </LayoutContainer>
   )
}

export default GroupHome
